import React from "react"
import styled from "styled-components"
import { rem } from "polished"

import { media } from "@sr/common"
import {
  Button,
  IconChevronRight,
  RegBody,
  SemiBdTitle3,
  SemiBdTitle4,
  SemiBdTitle5,
  SemiBdTitle6,
} from "@sr/gatsby-theme-ui"
import { Link } from "gatsby"

interface HardwareAlloyProps {
  showHero?: boolean
  showDivider?: boolean
  paddingTop?: [number, number, number]
}

const HardwareAlloy = ({
  showHero = true,
  showDivider = true,
  paddingTop = [32, 64, 88],
}: HardwareAlloyProps) => {
  return (
    <HardwareAlloyStyled paddingTop={paddingTop}>
      <div className="hardware-alloy__container">
        {showHero && (
          <>
            <div className="hardware-alloy__text-container">
              <SemiBdTitle6>Alloy SmartHome</SemiBdTitle6>
              <SemiBdTitle3>
                Hardware designed for smart communities
              </SemiBdTitle3>
              <RegBody>
                Our in-house smart home equipment does it all. Build the perfect
                smart apartments with Alloy hubs, thermostats, and sensors.
                Fully integrate them with the proptech you already use, and
                control everything with a single app.
              </RegBody>
            </div>
            <img
              className="hardware-alloy__img"
              src="https://res.cloudinary.com/smartrent-com/image/upload/q_auto,f_auto/v1733267019/pages/hardware/alloy_module_device-lineup_j97br6.png"
            />
           <Link className="hardware-alloy__learn-more-btn" to="/hardware/alloy/"> <Button>Learn More</Button></Link>
          </>
        )}
        <div className="hardware-alloy__alloy-text-container">
          {showDivider && (
            <div className="hardware-alloy__alloy-text-divider" />
          )}
          <SemiBdTitle4 className="hardware-alloy__alloy-text">
            Solutions powered by Alloy
          </SemiBdTitle4>
          {showDivider && (
            <div className="hardware-alloy__alloy-text-divider" />
          )}
        </div>
        <div className="hardware-alloy__cards-container">
          <div className="hardware-alloy__card-container">
            <div className="hardware-alloy__card-intro-container">
              <img src="https://res.cloudinary.com/smartrent-com/image/upload/v1717448471/pages/hardware/Vector_zzwzhv.svg_leotnh.svg" />
              <SemiBdTitle5>Access Control</SemiBdTitle5>
            </div>
            <p>
              Access control panels, readers and intercoms optimize all facets of guest and visitor entry, from common areas to apartment homes.
            </p>
            <Link to="/products/access-control/">Read More</Link>
          </div>
          <div className="hardware-alloy__card-container">
            <div className="hardware-alloy__card-intro-container">
              <img src="https://res.cloudinary.com/smartrent-com/image/upload/v1717448544/pages/hardware/Smart_Home_r1vinq.svg_mdoehw.svg" />
              <SemiBdTitle5>Smart Apartments</SemiBdTitle5>
            </div>
            <p>
              Smart locks, hubs, thermostats and leak sensors make it easy for
              owners to manage their communities while decreasing costs.
            </p>
            <Link to="/products/smart-apartments-homes/">Read More</Link>
          </div>
          <div className="hardware-alloy__card-container">
            <div className="hardware-alloy__card-intro-container">
              <img src="https://res.cloudinary.com/smartrent-com/image/upload/v1717448619/pages/hardware/Parking_Icon_lzkiv8.svg_fill_h9fah6.svg" />
              <SemiBdTitle5>Parking Management</SemiBdTitle5>
            </div>
            <p>
              Smart sensors paired with our software database help manage space
              alignment, streamline enforcement, and monetize guest or premium
              spaces.
            </p>
            <Link to="/products/smart-parking-management/">Read More</Link>
          </div>
          <div className="hardware-alloy__card-container">
            <div className="hardware-alloy__card-intro-container">
              <img src="https://res.cloudinary.com/smartrent-com/image/upload/v1717448664/pages/hardware/Door_Open_dydpwe.svg_fill_k7m0zs.svg" />
              <SemiBdTitle5>Self-Guided Tours</SemiBdTitle5>
            </div>
            <p>
              Strategically placed smart devices give prospects the freedom to
              tour a property when it’s convenient for them, effectively
              expanding leasing hours.
            </p>
            <Link to="/products/self-guided-tours/">Read More</Link>
          </div>
        </div>
        <div className="hardware-alloy__link-container">
          <Link to="/products/" className="hardware-alloy__link">
            <div className="hardware-alloy__link-icon-container">
              <IconChevronRight color="var(--color-primary)" />
            </div>
            <SemiBdTitle4 className="hardware-alloy__link-text">
              View all solutions
            </SemiBdTitle4>
          </Link>
        </div>
      </div>
    </HardwareAlloyStyled>
  )
}

export default HardwareAlloy

interface HardwareAlloySC {
  paddingTop?: [number, number, number]
}

const HardwareAlloyStyled = styled.section<HardwareAlloySC>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: left;
  padding: ${({ paddingTop }) =>
    paddingTop
      ? `${rem(paddingTop[0])} ${rem(16)} ${rem(40)}`
      : `${rem(32)} ${rem(16)} ${rem(40)}`};
  @media ${media.midUp} {
    padding: ${({ paddingTop }) =>
      paddingTop
        ? `${rem(paddingTop[1])} 0 ${rem(56)}`
        : `${rem(64)} 0 ${rem(56)}`};
  }

  @media ${media.stMidUp} {
    padding: ${({ paddingTop }) =>
      paddingTop ? `${rem(paddingTop[2])} 0` : `${rem(88)} 0`};
    text-align: center;
  }

  .hardware-alloy__container {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media ${media.midUp} {
      max-width: ${rem(616)};
    }

    @media ${media.stMidUp} {
      max-width: ${rem(1182)};
      justify-content: center;
      align-items: center;
    }

    .hardware-alloy__text-container {
      display: flex;
      flex-direction: column;
      gap: ${rem(8)};

      @media ${media.stMidUp} {
        gap: ${rem(16)};
        align-items: center;
      }

      h6 {
        color: var(--color-primary);
        font-size: ${rem(16)};
        line-height: ${rem(24)};

        @media ${media.midUp} {
          font-size: ${rem(20)};
          line-height: ${rem(24)};
        }
      }

      h3 {
        font-size: ${rem(32)};
        line-height: ${rem(40)};
        max-width: ${rem(343)};

        @media ${media.midUp} {
          max-width: ${rem(445)};
        }

        @media ${media.stMidUp} {
          max-width: ${rem(525)};
        }
      }

      p {
        @media ${media.midUp} {
          padding-top: ${rem(8)};
        }
        @media ${media.stMidUp} {
          max-width: ${rem(634)};
        }
      }
    }

    .hardware-alloy__img {
      margin-top: ${rem(40)};

      @media ${media.midUp} {
        margin-top: ${rem(32)};
      }

      @media ${media.stMidUp} {
        height: ${rem(336)};
        width: ${rem(641)};
      }
    }

    .hardware-alloy__learn-more-btn {
      margin: ${rem(24)} 0 ${rem(56)};
    }

    .hardware-alloy__alloy-text-container {
      @media ${media.midUp} {
        display: flex;
        justify-content: center;
        gap: ${rem(24)};
      }

      @media ${media.stMidUp} {
        gap: ${rem(48)};
      }

      .hardware-alloy__alloy-text-divider {
        display: none;

        @media ${media.midUp} {
          display: block;
          width: ${rem(68)};
          border-bottom: ${rem(2)} solid var(--color-gray300);
          margin-bottom: ${rem(16)};
        }

        @media ${media.stMidUp} {
          width: ${rem(333)};
        }
      }

      .hardware-alloy__alloy-text {
        text-align: center;
        font-size: ${rem(24)};
        line-height: ${rem(32)};

        @media ${media.midUp} {
          padding-top: ${rem(40)};
          font-size: ${rem(32)};
          line-height: ${rem(40)};
        }

        @media ${media.stMidUp} {
          padding-top: ${rem(32)};
        }
      }
    }

    .hardware-alloy__cards-container {
      display: flex;
      flex-direction: column;
      gap: ${rem(24)};
      padding-top: ${rem(24)};

      @media ${media.stMidUp} {
        flex-direction: row;
        flex-wrap: wrap;
        gap: ${rem(48)};
        padding-top: ${rem(48)};
        text-align: left;
      }

      .hardware-alloy__card-container {
        display: flex;
        flex-direction: column;
        gap: ${rem(16)};
        border-radius: ${rem(8)};
        padding: ${rem(24)};
        box-shadow: 0 ${rem(4)} ${rem(8)} rgba(45, 50, 57, 0.2);

        @media ${media.midUp} {
          padding: ${rem(32)} ${rem(56)};
        }

        .hardware-alloy__card-intro-container {
          display: flex;
          gap: ${rem(16)};

          img {
            @media ${media.midUp} {
              width: ${rem(32)};
              height: ${rem(32)};
            }
          }

          h5 {
            color: var(--color-primary);

            @media ${media.midUp} {
              font-size: ${rem(24)};
              line-height: ${rem(32)};
            }
          }
        }

        p {
          @media ${media.midUp} {
            max-width: ${rem(455)};
          }
        }

        a {
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: ${rem(6)};
          max-width: ${rem(128)};

          @media ${media.midUp} {
            font-size: ${rem(20)};
            line-height: ${rem(24)};
          }

          @media ${media.stMidUp} {
            padding-top: ${rem(16)};
          }
        }
      }
    }

    .hardware-alloy__link-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-top: ${rem(40)};

      @media ${media.midUp} {
        padding-top: ${rem(56)};
      }

      @media ${media.stMidUp} {
        padding-top: ${rem(48)};
      }

      .hardware-alloy__link {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: ${rem(8)};
        color: var(--color-primary);
        text-decoration: none;
      }

      .hardware-alloy__link-icon-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: ${rem(32)};
        height: ${rem(32)};
        border: ${rem(1)} solid var(--color-primary);
        border-radius: ${rem(32)};
      }

      .hardware-alloy__link-text {
        padding: 0;

        @media ${media.stMidUp} {
          font-size: ${rem(20)};
        }
      }
    }
  }
`
